import { useEffect, useRef } from 'react';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Proxy } from '#~/agent/types.js';
import { useAnalytics } from '#~/analytics/index.js';
import {
  createAlertTracker,
  ALERT_ID,
  ALERT_VARIANT,
  ALERT_ACTION
} from '#~/analytics/domains/alert-lifecycle.js';
import { resolveBuildVersion } from '#~/analytics/utils/build-version.js';
import { config } from '#~/config/index.js';
import { depot } from '#~/depot/index.js';
import { useLinkedAgentIDSelector } from '#~/electron/identity-store.js';
import { isElectronApp } from '#~/electron/utils.js';
import {
  getIncident,
  raise,
  resolve,
  subscribeIncidents,
  supersede,
  type IncidentKey
} from '#~/notifications/incidents.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';

/** Unknown/development versions are not evidence that a Bridge needs updating. */
export const isBridgeOutdated = (
  bridgeVersion: string | undefined,
  studioVersion: string
): boolean | undefined => {
  // Release builds use v?major.minor.patch (optionally prerelease/build metadata).
  // Date/SHA development stamps cannot be compared to release versions.
  const release =
    /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*)?(?:\+[\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*)?$/;
  const bridge = release.exec(bridgeVersion ?? '');
  const studio = release.exec(studioVersion);
  if (!bridge || !studio) return undefined;
  const [bridgeMajor, bridgeMinor, studioMajor, studioMinor] = [
    Number(bridge[1]),
    Number(bridge[2]),
    Number(studio[1]),
    Number(studio[2])
  ];
  if (![bridgeMajor, bridgeMinor, studioMajor, studioMinor].every(Number.isSafeInteger))
    return undefined;
  // ENG-4869, decided Oct 28: any major difference, or two minor versions behind.
  return bridgeMajor !== studioMajor || studioMinor - bridgeMinor >= 2;
};

const selectVersion = (state: T_Maybe<State>) => ({ version: state?.status?.encoder?.version });

// Dismissed notices stay in the store for history; only an open toast collides with N10.
const isUpdateNoticeOpen = (): boolean => {
  const entry = depot.getState().notifications['update-available'];
  return entry !== undefined && !entry[2].isClosed;
};

/** N10 uses the shared incident's per-Bridge, in-memory once-per-session policy. */
export const useBridgeVersionNotice = (proxy: Proxy, openUpdate: () => void): void => {
  const versions = useMultiProxyStateQuery<{ version: string | undefined }>(selectVersion);
  const [localAgentId, identityLoaded] = useLinkedAgentIDSelector();
  const { enabled, loading } = useFeatureFlag('alert_n10_enabled', { defaultValue: true });
  const { track } = useAnalytics();
  const latest = useRef({ track, openUpdate });
  latest.current = { track, openUpdate };
  const bridgeVersion = versions[proxy.id]?.version;
  const studioVersion = resolveBuildVersion(config.appVersion);
  const { id, roomId, name } = proxy;

  useEffect(() => {
    const key: IncidentKey = { alert: ALERT_ID.N10, entity: id };
    const outdated = isBridgeOutdated(bridgeVersion, studioVersion);
    const local = isElectronApp() && localAgentId === id;
    const variant = local ? ALERT_VARIANT.LOCAL : ALERT_VARIANT.REMOTE;
    let stopPendingTelemetry: (() => void) | undefined;

    const reconcile = () => {
      if (outdated === false) {
        resolve(key);
        stopPendingTelemetry?.();
        return;
      }
      if ((!loading && enabled === false) || isUpdateNoticeOpen()) {
        supersede(key);
        stopPendingTelemetry?.();
        return;
      }
      // A remounted flag hook starts loading again; that is not a kill-switch
      // decision and must not close an already delivered notice.
      if (loading || enabled !== true) return;
      // Do not spend the session allowance on an unknown version or local identity.
      if (outdated !== true || (isElectronApp() && !identityLoaded)) return;
      if (getIncident(key)?.state === 'showing') return;

      const tracker = createAlertTracker((name, category, properties) =>
        latest.current.track(name, category, properties)
      );
      let unsubscribeSupersession = () => {};
      const unsubscribe = subscribeIncidents((event) => {
        if (event.incident.showingId !== showing?.showingId) return;
        const context = {
          alert_id: ALERT_ID.N10,
          alert_variant: variant,
          showing_id: event.incident.showingId,
          studio_id: roomId,
          agent_id: id
        };
        if (event.type === 'shown') {
          stopPendingTelemetry = undefined;
          // The notice can outlive this room's watchdog. Keep its collision
          // observer with the showing, just like its close/action telemetry.
          unsubscribeSupersession = depot.subscribe(() => {
            if (isUpdateNoticeOpen()) supersede(key);
          });
          tracker.trackAlertShown(context);
        } else if (event.type === 'closed') {
          tracker.trackAlertClosed(context, event.reason);
          unsubscribeSupersession();
          unsubscribe();
        } else if (event.action === ALERT_ACTION.UPDATE) {
          tracker.trackAlertActed(context, { action: ALERT_ACTION.UPDATE });
        }
      });
      const showing = raise(
        key,
        {
          severity: 'warning',
          title: local
            ? `Update ${vendorName()}`
            : `${vendorName()} needs an update on ${name ?? `${vendorName()} Bridge`}`,
          message: local
            ? 'This version is out of date, so some features may not work.'
            : "It's out of date, so some features may not work. Update it on that computer."
        },
        {
          once: true,
          variant,
          autoClose: false,
          announce: 'polite',
          ...(local
            ? {
                actions: [
                  {
                    action: ALERT_ACTION.UPDATE,
                    label: 'Update',
                    onClick: () => latest.current.openUpdate()
                  }
                ]
              }
            : {})
        }
      );
      if (!showing) unsubscribe();
      else stopPendingTelemetry = unsubscribe;
    };

    // React alone would permit both notices for a frame. Observe the pinned id
    // synchronously so Update available supersedes N10 before the store is painted.
    let updateAvailable = isUpdateNoticeOpen();
    const unsubscribeStore = depot.subscribe(() => {
      const next = isUpdateNoticeOpen();
      if (next !== updateAvailable) {
        updateAvailable = next;
        reconcile();
      }
    });
    reconcile();
    return () => {
      unsubscribeStore();
      // Delivered notices own their telemetry until closed, even across a room
      // remount. Cancel only an undelivered showing; it has spent no allowance.
      if (stopPendingTelemetry) {
        supersede(key);
        stopPendingTelemetry();
      }
    };
  }, [
    id,
    roomId,
    name,
    bridgeVersion,
    studioVersion,
    localAgentId,
    identityLoaded,
    loading,
    enabled
  ]);
};
